import { useState } from "react";
import AnimatedSection from "@/components/AnimatedSection";

interface FAQItem {
  q: string;
  a: string;
}

interface FAQSectionProps {
  heading?: string;
  items: FAQItem[];
}

export default function FAQSection({ heading = "Frequently Asked Questions", items }: FAQSectionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="py-20 md:py-28 bg-background-100">
      <div className="container-astro max-w-3xl mx-auto">
        <AnimatedSection>
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
              {heading}
            </h2>
            <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
          </div>
        </AnimatedSection>

        <div className="space-y-3">
          {items.map((item, i) => (
            <AnimatedSection key={i} delay={i * 80}>
              <div
                className={`rounded-xl border transition-all duration-300 ${
                  openIndex === i
                    ? "border-accent-300/40 bg-background-50 shadow-lg shadow-accent-500/5"
                    : "border-background-200/60 bg-background-50 hover:border-accent-200/30"
                }`}
              >
                <button
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 md:px-6 md:py-5 text-left"
                  onClick={() => setOpenIndex(openIndex === i ? null : i)}
                >
                  <span className="text-sm md:text-base font-semibold text-foreground-950 pr-4">
                    {item.q}
                  </span>
                  <span
                    className={`w-8 h-8 flex items-center justify-center rounded-full bg-primary-50 text-primary-600 shrink-0 transition-all duration-300 ${
                      openIndex === i ? "bg-accent-500 text-background-50 rotate-180" : ""
                    }`}
                  >
                    <i className="ri-arrow-down-s-line text-lg" />
                  </span>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openIndex === i ? "max-h-[400px]" : "max-h-0"
                  }`}
                >
                  <div className="px-5 pb-5 md:px-6 md:pb-6 text-sm text-foreground-600 leading-relaxed">
                    {item.a}
                  </div>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
}